import { Point } from '@arcgis/core/geometry.js';
import { locationToAddress } from '@arcgis/core/rest/locator';
import * as Sentry from '@sentry/react';
import {
  AssetType,
  FeatureAttributes,
  Location,
  NearestApiResponse,
  emptyLocation,
} from '@types';
import axios from 'axios';
import * as ELG from 'esri-leaflet';
import L, {
  Icon,
  LatLngExpression,
  LatLngTuple,
  LayerGroup,
  LeafletMouseEvent,
  Map,
} from 'leaflet';
import { isMobile } from 'react-device-detect';
import proj4 from 'proj4';

export const WCC_BOUNDARY_LAYER =
  'https://www.rbkc.gov.uk/arcgis/rest/services/WCC/FixMyStreetOpen/MapServer/4';

export const GEOCODE_SERVER = import.meta.env.VITE_MAINSITE_GEOCODE_SERVER;

export const initialCenterWCC = [51.511422, -0.151843] as LatLngTuple;

const LOCATION_ENDPOINT =
  import.meta.env.VITE_APIM_BASE_URL + '/locations/v1/find';
const NEAREST_ENDPOINT =
  import.meta.env.VITE_APIM_BASE_URL + '/locations/v1/nearest';

const WITHIN_BOUNDARY_ENDPOINT =
  import.meta.env.VITE_APIM_BASE_URL + '/gis-service/v1/within-boundary';

const NEAREST_ASSET_ENDPOINT =
  import.meta.env.VITE_APIM_BASE_URL + '/gis-service/v1/find-nearest-asset';

const apiKey = import.meta.env.VITE_APIM_API_KEY;

export const boroughBoundaryLayer = ELG.featureLayer({
  url: WCC_BOUNDARY_LAYER,
  simplifyFactor: 0.5,
  precision: 5,
  style(feature, layer) {
    return { color: '#5897fcad', weight: 2 };
  },
});

export const markerIcon = new Icon({
  iconUrl:
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
      <path fill="#0067DD" stroke="white" stroke-width="2" d="M16,3.5c-4.142,0-7.5,3.358-7.5,7.5c0,4.143,7.5,18.121,7.5,18.121S23.5,15.143,23.5,11C23.5,6.858,20.143,3.5,16,3.5z M16,14.584c-1.979,0-3.584-1.604-3.584-3.584S14.021,7.416,16,7.416S19.584,9.021,19.584,11S17.979,14.584,16,14.584z"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

export const dropMarkerOnMap = (
  event: LeafletMouseEvent,
  address: addressResponseType,
  mapMethods: MapMethods,
) => {
  const { addMarker, clearGraphics, map } = mapMethods;
  clearGraphics();
  const { lat, lng } = event.latlng;
  const popupContent = `<div padding: 10px; border-radius: 5px;">
            <span>${address.full_address}</span></div>`;
  map.flyTo([lat, lng]);
  addMarker([lat, lng]);
};

function shimmer() {
  // create shimmer container
  const container = document.createElement('div');
  const child = document.createElement('div');
  container.appendChild(child);
  // create stylesheet
  const styleEl = document.createElement('style');
  document.head.appendChild(styleEl);
  const styleSheet = styleEl.sheet as CSSStyleSheet;
  const keyFrames = `@keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }`;
  styleSheet.insertRule(keyFrames, styleSheet.cssRules.length);
  container.style.cssText = `
    width: 100%;
    height: 38px;
    border-radius:5px;
    background-color: #ddd;
    position:relative;
    overflow:hidden;
  `;
  child.style.cssText = `
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, #ddd 0%, #f5f5f5 50%, #ddd 100%);
    background-repeat: no-repeat;
    background-size: 200% 100%;
    animation: loading 1.0s infinite;
    position:absolute;
  `;
  return container;
}

export const loadingPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(shimmer())
    .openOn(map);
};

export const outsideBoroughPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>You selected a location outside the borough, try again</span></div>`,
    )
    .openOn(map);
};

export const errorZoomInPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>Zoom in closer to select a accurate location</span></div>`,
    )
    .openOn(map);
};

export const geolocationOutOfBoroughPopup = (map: Map) => {
  map.flyTo(initialCenterWCC, 13);
  L.popup({ closeOnClick: true })
    .setLatLng(initialCenterWCC)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>Your location needs to be inside the borough.</span></div>`,
    )
    .openOn(map);
};

export const tflBoundaryPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFD420; padding: 10px; border-radius: 5px;">
            <span>Westminster City Council does not maintain this road. It is managed by Transport for London (TfL)</span></div>`,
    )
    .openOn(map);
};

/**
 * The error message is specified in the story, refer to here: {@link https://westminstergov.atlassian.net/browse/RITE-1248}
 */
export const wasteAddressErrorPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>We cannot retrieve an address and may not provide waste related services for this location. If you want to try again, please select a different location.</span></div>`,
    )
    .openOn(map);
};

export const addressErrorPopup = (map: Map, event?: LeafletMouseEvent) => {
  const popupLocation: LatLngExpression = event?.latlng || initialCenterWCC;
  const popupContent = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>We cannot retrieve an address for this location. If you want to try again, please select a different location.</span></div>`;

  L.popup({ closeOnClick: true })
    .setLatLng(popupLocation)
    .setContent(popupContent)
    .openOn(map);
};

export const isMapCorrectZoomLevel = (map: Map) => map.getZoom() > 16;

export const isCoordinatesInsideBorough = async (coordinates: LatLngTuple) => {
  const isInside = await getWithinBoundaryCheck(coordinates[0], coordinates[1]);
  return isInside;
};

export const getAddressFromMapPoint = async (
  point: LatLngTuple,
): Promise<__esri.AddressCandidate | null> => {
  let address = null;
  const coords = new Point({ latitude: point[0], longitude: point[1] });
  await locationToAddress(
    GEOCODE_SERVER,
    {
      location: coords,
      // @ts-expect-error "wkid" is the only required property in the spatialReference object to output the correct coordinates
      outSpatialReference: { wkid: 4326 },
    },
    500,
  )
    .then((response) => {
      address = response;
      console.log({ address });
    })
    .catch((e) => {
      console.log({ e });
      address = null;
    });
  return address;
};

export type addressResponseType = {
  address_line_1: string;
  address_line_2: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
  full_address: string;
  asset?: FeatureAttributes;
  easting?: string;
  northing?: string;
};

const containsAddressFilterItem = (address: string) => {
  const addressLookupFilter = [
    'PAY PHONE',
    'PAYPHONE',
    'BUS STOP',
    'BUS SHELTER',
    'BUS SHELTERS',
  ];
  return addressLookupFilter.find((filterItem) => address.includes(filterItem));
};

interface GetResultsHandler {
  suggestTerm: string;
  sourceIndex: number;
}

export const handleGetSuggestions = async (params: GetResultsHandler) => {
  const requestUrl = `${LOCATION_ENDPOINT}?query=${encodeURIComponent(
    params.suggestTerm,
  )}&fq=LOCAL_CUSTODIAN_CODE:5990&custom_sort=S`;
  return await axios
    .get(requestUrl, { headers: { 'Ocp-Apim-Subscription-Key': apiKey } })
    .then((response) => {
      if (
        response.data.results === undefined ||
        response.data.results.length === 0
      ) {
        console.warn('No suggestions found');
        return [];
      }
      return response.data.results
        .filter((suggestion: addressResponseType) => suggestion !== null)
        .filter(
          (suggestion: addressResponseType) =>
            !containsAddressFilterItem(suggestion.address_line_1),
        )
        .map((suggestion: addressResponseType) =>
          removeStreetRecordLabelFromResults(suggestion),
        );
    })
    .catch((error) => {
      Sentry.captureException(error);
      return [];
    });
};

export const getNearestAddress = async (
  latitude: number,
  longitude: number,
): Promise<addressResponseType | null> => {
  try {
    const requestUrl = `${NEAREST_ENDPOINT}?point=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}&fq=LOCAL_CUSTODIAN_CODE:5990`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data.results && response.data.results.length > 0) {
      // Projections to get eastings and northings
      const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
      const osgb36 =
        '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +datum=OSGB36 +units=m +no_defs';
      const [easting, northing] = proj4(wgs84, osgb36, [longitude, latitude]);

      //APIM doesn't accept floats, only the first half is required
      const eastingString = easting.toString().split('.')[0];
      const northingString = northing.toString().split('.')[0];
      const latitudeString = latitude.toString();
      const longitudeString = longitude.toString();

      const result = removeStreetRecordLabelFromResults(
        response.data.results[0],
      );
      return {
        ...result,
        latitude: latitudeString,
        longitude: longitudeString,
        easting: eastingString,
        northing: northingString,
      };
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getNearestAsset = async (
  latitude: number,
  longitude: number,
  assetType: AssetType,
): Promise<NearestApiResponse | null> => {
  try {
    const requestUrl = `${NEAREST_ASSET_ENDPOINT}?latitude=${latitude}&longitude=${longitude}&layer=${assetType}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getWithinBoundaryCheck = async (
  latitude: number,
  longitude: number,
) => {
  try {
    const requestUrl = `${WITHIN_BOUNDARY_ENDPOINT}?latitude=${latitude}&longitude=${longitude}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export interface MapMethods {
  clearGraphics: () => void;
  addMarker: (latlng: LatLngTuple, popupContent?: string | undefined) => void;
  onChange: (location: addressResponseType) => void;
  map: Map;
  assetType?: AssetType | undefined;
  graphicsLayer: React.MutableRefObject<LayerGroup<any> | null>;
}

export interface MapMethodProps {
  mapMethods: MapMethods;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  assetType?: AssetType | undefined;
}

export const addAsset = async (
  assetType: AssetType | undefined,
  onChange: (location: addressResponseType) => void,
  address: addressResponseType,
) => {
  let withinTflBoundary = false;
  let assetAttributes: FeatureAttributes | undefined;
  if (assetType !== undefined) {
    const asset = await getNearestAsset(
      Number.parseFloat(address.latitude),
      Number.parseFloat(address.longitude),
      assetType,
    );
    if (asset?.features) {
      [assetAttributes] = asset.features
        .filter((feature) => feature.attributes)
        .map((feature) => feature.attributes);
    }

    if (asset?.within_tfl_boundary) {
      withinTflBoundary = true;
    }
  }

  if (assetAttributes && assetType) {
    const {
      central_asset_id,
      contract_area_code,
      feature_type_code,
      site_class_code,
      site_class_name,
    } = assetAttributes;

    onChange({
      ...address,
      address_line_1: assetAttributes.site_name ?? '',
      address_line_2: '',
      postcode: '',
      town: '',
      UPRN: '',
      USRN: '',
      full_address: assetAttributes.site_name ?? '',
      asset: {
        central_asset_id,
        contract_area_code,
        feature_type_code,
        site_class_code,
        site_class_name,
        layer: assetType,
      },
    });
  } else {
    onChange(address);
  }
  return { withinTflBoundary };
};

export const onMapClick = async (
  event: LeafletMouseEvent,
  mapMethods: MapMethods,
  setSearchTerm?: (searchTerm: string) => void,
) => {
  const { map, onChange, assetType, clearGraphics } = mapMethods;
  const { lat, lng } = event.latlng;
  const point = [lat, lng] as LatLngTuple;
  setSearchTerm && setSearchTerm('');
  clearGraphics();
  loadingPopup(event, map);

  const isHighwaysProcess =
    assetType === 'HighwaysCarriageways' || assetType === 'HighwaysFootways';

  const correctZoomLevel = isMapCorrectZoomLevel(map);
  if (!correctZoomLevel) {
    errorZoomInPopup(event, map);
    return;
  }

  try {
    const inBorough = await isCoordinatesInsideBorough(point);
    if (!inBorough) {
      outsideBoroughPopup(event, map);
      return;
    }

    const addressFromPin = await getNearestAddress(lat, lng);
    if (addressFromPin === null) {
      addressErrorPopup(map, event);
      return;
    }

    const { withinTflBoundary } = await addAsset(
      assetType,
      onChange,
      addressFromPin,
    );

    if (isHighwaysProcess && withinTflBoundary) {
      tflBoundaryPopup(event, map);
      return;
    }
    map.closePopup();
    setSearchTerm && setSearchTerm(addressFromPin.full_address);
    dropMarkerOnMap(event, addressFromPin, mapMethods);
  } catch (error) {
    Sentry.captureException(error);
    addressErrorPopup(map, event);
  }
};

export const removeStreetRecordLabelFromResults = (
  address: addressResponseType,
) => {
  if (address.address_line_1 === 'STREET RECORD') {
    address.full_address = address.full_address.replace('STREET RECORD, ', '');
  }

  return address;
};
